import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, LogOut, ChevronRight, Clock, CheckCircle2, 
  AlertCircle, Download, Car, Calendar, MapPin, RefreshCw,
  Phone, Mail, Building2, FileCheck, Receipt, Upload, X, Image, Trash2, Plus
} from 'lucide-react';
import { getVersionString } from '../config/version';
import { LOGO_DARK_BG } from '../config/branding';
import CustomerChatWidget from '../components/CustomerChatWidget';
import ReportDamageModal from '../components/ReportDamageModal';
import useCustomerAuth from '../hooks/useCustomerAuth';

// Progress Bar Component
const ProgressBar = ({ progress, status }) => {
  const getColor = () => {
    if (progress >= 95) return 'bg-green-500';
    if (progress >= 60) return 'bg-blue-500';
    if (status === 'rueckfrage_offen') return 'bg-amber-500';
    return 'bg-brand-green';
  };

  return (
    <div className="w-full bg-gray-200 rounded-full h-2.5">
      <div 
        className={`h-2.5 rounded-full transition-all duration-500 ${getColor()}`}
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

// Status Badge Component
const StatusBadge = ({ status, label }) => {
  const getStyles = () => {
    switch (status) {
      case 'abgeschlossen':
      case 'gutachten_fertig':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rueckfrage_offen':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'eingegangen':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'termin_vereinbart':
      case 'begutachtung_durchgefuehrt':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'gutachten_in_erstellung':
        return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <span className={`px-3 py-1.5 rounded-lg text-xs font-bold border-2 ${getStyles()}`}>
      {label}
    </span>
  );
};

// Timeline Item Component
const TimelineItem = ({ event, isLast }) => {
  const getIcon = () => {
    switch (event.type) {
      case 'case_submitted':
        return <FileCheck className="w-4 h-4" />;
      case 'customer_invited':
        return <Mail className="w-4 h-4" />;
      case 'status_changed':
        return <RefreshCw className="w-4 h-4" />;
      case 'document_uploaded':
        return <Download className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center">
        <div className="w-8 h-8 rounded-full bg-brand-green/10 flex items-center justify-center text-brand-green">
          {getIcon()}
        </div>
        {!isLast && <div className="w-0.5 h-full bg-gray-200 my-1" />}
      </div>
      <div className="pb-6">
        <p className="font-semibold text-gray-900 text-sm">{event.title}</p>
        <p className="text-sm text-gray-600">{event.message}</p>
        <p className="text-xs text-gray-400 mt-1">{formatDate(event.created_at)}</p>
      </div>
    </div>
  );
};

// Case Detail Modal Component
const CaseDetailModal = ({ caseData, onClose, customerToken, onRefresh }) => {
  const [uploading, setUploading] = useState(false);
  const [customerUploads, setCustomerUploads] = useState([]);
  const fileInputRef = useRef(null);
  
  // Load uploads on mount
  useEffect(() => {
    if (!caseData) return;
    
    const loadUploads = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/kunden/cases/${caseData.id}/uploads`,
          { headers: { 'Authorization': `Bearer ${customerToken}` } }
        );
        if (res.ok) {
          const data = await res.json();
          setCustomerUploads(data.uploads || []);
        }
      } catch (e) {
        console.error('Load uploads failed:', e);
      }
    };
    
    loadUploads();
  }, [caseData?.id, customerToken]);
  
  if (!caseData) return null;

  const refreshUploads = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/kunden/cases/${caseData.id}/uploads`,
        { headers: { 'Authorization': `Bearer ${customerToken}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setCustomerUploads(data.uploads || []);
      }
    } catch (e) {
      console.error('Refresh uploads failed:', e);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      alert('Ungültiger Dateityp. Erlaubt: PDF, JPG, PNG');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Datei zu groß. Maximum: 10MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/kunden/cases/${caseData.id}/upload`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${customerToken}` },
          body: formData
        }
      );

      const result = await res.json();
      if (result.success) {
        alert('Dokument erfolgreich hochgeladen!');
        refreshUploads();
        if (onRefresh) onRefresh();
      } else {
        alert('Fehler: ' + (result.detail || result.error || 'Upload fehlgeschlagen'));
      }
    } catch (error) {
      alert('Fehler beim Upload: ' + error.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const deleteUpload = async (uploadId) => {
    if (!window.confirm('Upload wirklich löschen?')) return;
    
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/kunden/cases/${caseData.id}/uploads/${uploadId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${customerToken}` }
        }
      );
      if (res.ok) {
        refreshUploads();
      }
    } catch (e) {
      console.error('Delete failed:', e);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getDocIcon = (type) => {
    switch (type) {
      case 'gutachten': return <FileText className="w-5 h-5" />;
      case 'abtretungserklaerung': return <FileCheck className="w-5 h-5" />;
      case 'rechnung': return <Receipt className="w-5 h-5" />;
      default: return <FileText className="w-5 h-5" />;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-slate-900 px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">{caseData.license_plate || 'Kennzeichen nicht erfasst'}</h2>
              <p className="text-gray-400 text-sm">{caseData.vehicle}</p>
            </div>
            <StatusBadge status={caseData.status} label={caseData.status_label} />
          </div>
        </div>

        {/* Progress */}
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Fortschritt</span>
            <span className="text-sm font-bold text-brand-green">{caseData.progress}%</span>
          </div>
          <ProgressBar progress={caseData.progress} status={caseData.status} />
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[60vh] p-6">
          {/* Info Grid */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <Calendar className="w-4 h-4" />
                <span className="text-xs">Schadensdatum</span>
              </div>
              <p className="font-semibold text-gray-900">
                {caseData.damage_date ? new Date(caseData.damage_date).toLocaleDateString('de-DE') : 'N/A'}
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <MapPin className="w-4 h-4" />
                <span className="text-xs">Schadensort</span>
              </div>
              <p className="font-semibold text-gray-900 truncate">{caseData.damage_location || 'N/A'}</p>
            </div>
          </div>

          {/* Documents Section */}
          {caseData.documents && caseData.documents.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Download className="w-4 h-4 text-brand-green" />
                Verfügbare Dokumente
              </h3>
              <div className="space-y-2">
                {caseData.documents.map((doc, idx) => (
                  <a
                    key={idx}
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-xl hover:bg-green-100 transition-colors group"
                    data-testid={`download-${doc.type}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center text-green-600 group-hover:bg-green-200 transition-colors">
                        {getDocIcon(doc.type)}
                      </div>
                      <span className="font-medium text-gray-900">{doc.name}</span>
                    </div>
                    <Download className="w-5 h-5 text-green-600" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Customer Upload Section */}
          <div className="mb-6">
            <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Upload className="w-4 h-4 text-brand-green" />
              Eigene Dokumente hochladen
            </h3>
            
            {/* Upload Button */}
            <div className="mb-4">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="w-full p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-brand-green hover:bg-green-50 transition-all flex flex-col items-center gap-2 text-gray-600 hover:text-brand-green disabled:opacity-50"
                data-testid="upload-btn"
              >
                {uploading ? (
                  <>
                    <RefreshCw className="w-6 h-6 animate-spin" />
                    <span className="text-sm font-medium">Wird hochgeladen...</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-6 h-6" />
                    <span className="text-sm font-medium">Dokument oder Foto hochladen</span>
                    <span className="text-xs text-gray-400">PDF, JPG, PNG (max. 10MB)</span>
                  </>
                )}
              </button>
            </div>

            {/* Uploaded Files List */}
            {customerUploads.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-gray-500 mb-2">Ihre hochgeladenen Dateien:</p>
                {customerUploads.map((upload) => (
                  <div
                    key={upload.id}
                    className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-xl group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center text-gray-600">
                        {upload.content_type?.includes('image') ? (
                          <Image className="w-5 h-5" />
                        ) : (
                          <FileText className="w-5 h-5" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 text-sm truncate max-w-[180px]">{upload.filename}</p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(upload.size)} • {new Date(upload.uploaded_at).toLocaleDateString('de-DE')}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => deleteUpload(upload.id)}
                      className="text-gray-400 hover:text-red-500 p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Löschen"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Partner Contact */}
          {caseData.partner && (
            <div className="mb-6">
              <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-brand-green" />
                Ihr Ansprechpartner
              </h3>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="font-semibold text-gray-900">{caseData.partner.company}</p>
                {caseData.partner.contact && <p className="text-sm text-gray-600">{caseData.partner.contact}</p>}
                <div className="flex items-center gap-4 mt-2">
                  {caseData.partner.phone && (
                    <a href={`tel:${caseData.partner.phone}`} className="flex items-center gap-1 text-sm text-brand-green hover:underline">
                      <Phone className="w-4 h-4" />
                      {caseData.partner.phone}
                    </a>
                  )}
                  {caseData.partner.email && (
                    <a href={`mailto:${caseData.partner.email}`} className="flex items-center gap-1 text-sm text-brand-green hover:underline">
                      <Mail className="w-4 h-4" />
                      E-Mail
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Timeline */}
          {caseData.timeline && caseData.timeline.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4 text-brand-green" />
                Verlauf
              </h3>
              <div className="pl-2">
                {[...caseData.timeline].reverse().map((event, idx) => (
                  <TimelineItem 
                    key={idx} 
                    event={event} 
                    isLast={idx === caseData.timeline.length - 1} 
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-colors"
            data-testid="close-case-detail"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Component
const KundenDashboardPage = () => {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCase, setSelectedCase] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const customerName = localStorage.getItem('customer_name');
  const customerToken = localStorage.getItem('customer_token');
  
  // Auto Token Refresh Hook - hält Kunden dauerhaft eingeloggt
  const { handleLogout: authLogout } = useCustomerAuth();

  useEffect(() => {
    if (!customerToken) {
      navigate('/kunden/login');
      return;
    }
    loadCases();
  }, [customerToken, navigate]);

  const loadCases = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/kunden/cases`, {
        headers: { 'Authorization': `Bearer ${customerToken}` }
      });
      
      if (response.status === 401) {
        handleLogout();
        return;
      }
      
      const data = await response.json();
      setCases(data.cases || []);
    } catch (error) {
      console.error('Load failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCaseDetail = async (caseId) => {
    setLoadingDetail(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/kunden/cases/${caseId}`, {
        headers: { 'Authorization': `Bearer ${customerToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedCase(data);
      }
    } catch (error) {
      console.error('Load case detail failed:', error);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleLogout = () => {
    // Nutze die zentrale Auth-Logout-Funktion
    authLogout();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-slate-900 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <img src={LOGO_DARK_BG} alt="Dein Gutachten Direkt" className="h-8 sm:h-10" />
              <div className="hidden sm:block h-8 w-px bg-slate-700" />
              <h1 className="text-lg sm:text-2xl font-bold text-white">Meine Gutachten</h1>
            </div>
            <div className="flex items-center justify-between sm:justify-end gap-4 sm:gap-6">
              <button
                onClick={() => setShowReportModal(true)}
                className="bg-brand-green hover:bg-green-600 text-white px-4 py-2 rounded-xl font-semibold text-sm inline-flex items-center gap-2 transition-colors"
                data-testid="report-damage-btn"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Neuen Schaden melden</span>
                <span className="sm:hidden">Neu</span>
              </button>
              <span className="text-sm text-gray-300 hidden sm:inline">Hallo, {customerName}</span>
              <button
                onClick={handleLogout}
                className="text-gray-300 hover:text-white text-sm font-medium inline-flex items-center gap-2 transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Abmelden</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12 pb-20">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{cases.length}</p>
                <p className="text-xs sm:text-sm text-gray-500">Gesamt</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">
                  {cases.filter(c => !['abgeschlossen', 'gutachten_fertig'].includes(c.status)).length}
                </p>
                <p className="text-xs sm:text-sm text-gray-500">In Bearbeitung</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">
                  {cases.filter(c => ['abgeschlossen', 'gutachten_fertig'].includes(c.status)).length}
                </p>
                <p className="text-xs sm:text-sm text-gray-500">Abgeschlossen</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Download className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">
                  {cases.filter(c => c.has_gutachten).length}
                </p>
                <p className="text-xs sm:text-sm text-gray-500">Downloads</p>
              </div>
            </div>
          </div>
        </div>

        {/* Cases List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-4 border-brand-green border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-500">Lade Ihre Akten...</p>
          </div>
        ) : cases.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-2xl p-12 text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Car className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Noch keine Gutachten</h3>
            <p className="text-gray-600 max-w-md mx-auto">
              Sobald ein Gutachten für Sie angelegt wurde, erscheint es hier. Sie werden per E-Mail benachrichtigt.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {cases.map(c => (
              <div 
                key={c.id} 
                className="bg-white border-2 border-gray-200 rounded-2xl p-4 sm:p-6 hover:border-brand-green/40 hover:shadow-lg transition-all cursor-pointer group"
                onClick={() => loadCaseDetail(c.id)}
                data-testid={`case-card-${c.id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  {/* Left: Info */}
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Car className="w-6 h-6 sm:w-7 sm:h-7 text-slate-600" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <p className="text-lg sm:text-xl font-bold text-gray-900">{c.license_plate || 'Kennzeichen nicht erfasst'}</p>
                        <StatusBadge status={c.status} label={c.status_label} />
                      </div>
                      <p className="text-sm text-gray-600 mt-0.5">{c.vehicle}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {c.damage_date ? new Date(c.damage_date).toLocaleDateString('de-DE') : 'N/A'}
                        </span>
                        {c.damage_location && (
                          <span className="flex items-center gap-1 truncate max-w-[150px]">
                            <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                            {c.damage_location}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right: Progress + Action */}
                  <div className="flex items-center gap-4 sm:gap-6">
                    <div className="flex-1 sm:w-40">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-500">Fortschritt</span>
                        <span className="text-xs font-bold text-brand-green">{c.progress}%</span>
                      </div>
                      <ProgressBar progress={c.progress} status={c.status} />
                    </div>
                    <div className="flex items-center gap-2">
                      {c.has_gutachten && (
                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center" title="Gutachten verfügbar">
                          <Download className="w-4 h-4 text-green-600" />
                        </div>
                      )}
                      <div className="w-8 h-8 bg-brand-green/10 rounded-lg flex items-center justify-center group-hover:bg-brand-green group-hover:text-white text-brand-green transition-colors">
                        <ChevronRight className="w-5 h-5" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Case Detail Modal */}
      {selectedCase && (
        <CaseDetailModal 
          caseData={selectedCase} 
          onClose={() => setSelectedCase(null)}
          customerToken={customerToken}
          onRefresh={() => loadCaseDetail(selectedCase.id)}
        />
      )}

      {/* Loading Detail Overlay */}
      {loadingDetail && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-6 shadow-xl">
            <div className="w-8 h-8 border-4 border-brand-green border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-gray-600 mt-3">Lade Details...</p>
          </div>
        </div>
      )}

      {/* Chat Widget - show when viewing a case */}
      {selectedCase && (
        <CustomerChatWidget
          caseId={selectedCase.id}
          customerToken={customerToken}
          customerName={customerName}
        />
      )}

      {/* Report New Damage Modal */}
      <ReportDamageModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        customerToken={customerToken}
        onSuccess={(newCaseId) => {
          setShowReportModal(false);
          loadCases();
          // Optionally load the new case detail
          setTimeout(() => loadCaseDetail(newCaseId), 500);
        }}
      />

      {/* Version Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-gray-100 border-t border-gray-200 py-2 px-4 text-center">
        <span className="text-xs text-gray-500">Kundenportal • {getVersionString()}</span>
      </div>
    </div>
  );
};

export default KundenDashboardPage;
